import {skipToken} from '@reduxjs/toolkit/query';
import {showNotification} from 'platform/components';

import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  selectCurrentUserInfo,
  useGetUserAbsencesQuery,
  useGetUserAvailabilitySummaryQuery,
  useSetUserAwayMutation,
  useUpdateUserAbsenceMutation,
} from '@dms/api/customer';
import {UserAbsenceResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {SET_USER_AWAY_MUTATION_KEY} from '../../constants/SET_USER_AWAY_MUTATION_KEY';
import {UPDATE_USER_ABSENCE_MUTATION_KEY} from '../../constants/UPDATE_USER_ABSENCE_MUTATION_KEY';
import {AvailabilityStatus} from './AvailabilityStatus';
import {getActiveAbsence} from './getActiveAbsence';
import {getEffectiveAvailabilityStatus} from './getEffectiveAvailabilityStatus';
import {getPlannedAbsence} from './getPlannedAbsence';
import {getReturnEarlyRequestBody} from './getReturnEarlyRequestBody';

interface UseAvailabilityStatusResult {
  status: AvailabilityStatus;
  activeAbsence: UserAbsenceResponseBody | null;
  plannedAbsence: UserAbsenceResponseBody | null;
  isMutating: boolean;
  setAway: (isAway: boolean) => void;
  returnEarly: () => void;
}

export function useAvailabilityStatus(): UseAvailabilityStatusResult {
  const {data: userInfo} = useSelector(selectCurrentUserInfo);
  const userId = userInfo?.id;
  const isAway = userInfo?.isAway ?? false;

  const {data: summaries} = useGetUserAvailabilitySummaryQuery(
    isNotNil(userId) ? {userIds: [userId]} : skipToken
  );
  const {data: absences} = useGetUserAbsencesQuery(isNotNil(userId) ? {userId} : skipToken);

  const [setUserAway, {isLoading: isAwayLoading}] = useSetUserAwayMutation({
    fixedCacheKey: SET_USER_AWAY_MUTATION_KEY,
  });
  const [updateUserAbsence, {isLoading: isReturning}] = useUpdateUserAbsenceMutation({
    fixedCacheKey: UPDATE_USER_ABSENCE_MUTATION_KEY,
  });

  const nowIso = new Date().toISOString();
  const summary = summaries?.[0];
  const status = getEffectiveAvailabilityStatus(summary);
  const activeAbsence = getActiveAbsence(absences, nowIso);
  const plannedAbsence = getPlannedAbsence(absences, nowIso);

  const setAway = (nextIsAway: boolean) => {
    if (isNil(userId)) {
      return;
    }

    setUserAway({userId, isAway: nextIsAway})
      .unwrap()
      .then(() => {
        showNotification.success(
          nextIsAway
            ? i18n.t('user.awayStatus.toastAwayOn')
            : i18n.t('user.awayStatus.toastAwayOff')
        );
      })
      .catch(handleApiError);
  };

  const returnEarly = () => {
    if (isNil(userId) || isNil(activeAbsence)) {
      return;
    }

    const endedAt = new Date().toISOString();

    updateUserAbsence({
      userId,
      userAbsenceId: activeAbsence.id,
      userAbsenceRequestBody: getReturnEarlyRequestBody(activeAbsence, endedAt),
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.userAbsence.notifications.returnedEarly'));

        if (isAway) {
          setUserAway({userId, isAway: false}).unwrap().catch(handleApiError);
        }
      })
      .catch(handleApiError);
  };

  return {
    status,
    activeAbsence,
    plannedAbsence,
    isMutating: isAwayLoading || isReturning,
    setAway,
    returnEarly,
  };
}
